// Import des hooks React nécessaires
import { useState, useEffect } from "react";
import { logDev } from "../utils/logger.js";

// Import du service qui va chercher les contacts d'un groupe
import { fetchContactsByGroupe } from "../services/contactsService.js";

/**
 * Hook personnalisé pour charger et gérer les contacts d'un groupe.
 * @param {string} groupeId - L'identifiant du groupe dont on veut récupérer les contacts.
 * @returns {object} - Un objet contenant :
 *   - contacts : tous les contacts valides
 *   - filteredContacts : contacts filtrés selon la période dominante
 *   - periodeDominante : clé de la période dominante
 *   - setFilteredContacts : setter pour mettre à jour manuellement les contacts filtrés
 */

export const useContacts = (groupeId) => {
  // État pour stocker tous les contacts valides
  const [contacts, setContacts] = useState([]);

  // État pour stocker uniquement les contacts filtrés (selon la période dominante)
  const [filteredContacts, setFilteredContacts] = useState([]);

  // État pour stocker la période dominante détectée
  const [periodeDominante, setPeriodeDominante] = useState("");

  useEffect(() => {
    // Fonction interne asynchrone pour charger les contacts depuis l'API
    const chargerContacts = async () => {
      try {
        // Appel du service qui retourne un objet avec :
        // - contactsValid : liste nettoyée des contacts
        // - filteredContacts : liste filtrée selon la période dominante
        // - periodeDominante : clé de la période dominante
        const { contactsValid, filteredContacts, periodeDominante } =
          await fetchContactsByGroupe(groupeId);

          // 🔹 Log optimisé
          logDev("[useContacts] données reçues", {
            groupeId,
            nbContacts: contactsValid?.length || 0,
            nbFiltres: filteredContacts?.length || 0,
            periodeDominante,
            previewContacts: (contactsValid || []).slice(0, 3).map(c => ({
              id: c._id,
              nom: c.nom,
            mail: c.mail
         }))
        });

        // Mise à jour des états locaux avec les données reçues
        setContacts(contactsValid);
        setFilteredContacts(filteredContacts);
        setPeriodeDominante(periodeDominante);

      } catch (err) {
        // Gestion d'erreur : log dans la console
        logDev("[useContacts] erreur chargement contacts", {
          groupeId,
          message: err.message,
          stack: err.stack
        });
        setContacts([]);
        setFilteredContacts([]);
        setPeriodeDominante("");
      }
    };

    // On ne lance le chargement que si un groupeId est fourni
    if (groupeId) {
      logDev("[useContacts] lancement chargement contacts", { groupeId });
      chargerContacts();
    }
  }, [groupeId]); // Le hook se relance si le groupeId change

  // On retourne les données et le setter pour filteredContacts
  return { contacts, filteredContacts, periodeDominante, setFilteredContacts };
};
